import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { Accessibility, Link2, MailCheck, Plus, Radio, Shield, Table2, Utensils } from "lucide-react"
import type { ReactNode } from "react"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import { formsQueryOptions } from "@/core/forms"
import {
  eventAccessibilityReportQueryOptions,
  eventDietaryReportQueryOptions,
  guestsQueryOptions,
} from "@/core/people"
import { commsReportQueryOptions } from "@/core/comms-report"
import { reportDatasetQueryOptions, savedReportsQueryOptions } from "@/core/reports"
import { CustomReports } from "@/components/reports/CustomReports"
import { DietaryReport } from "@/components/reports/DietaryReport"
import { RsvpReport } from "@/components/reports/RsvpReport"
import { CommsReport } from "@/components/reports/CommsReport"
import { SystemFormReport } from "@/components/reports/SystemFormReport"
import { AccessibilityReport } from "@/components/reports/AccessibilityReport"
import { SharedAccessPanel } from "@/components/reports/SharedAccessPanel"
import { EMERGENCY_REPORT } from "@/components/reports/report-configs"
import { cn } from "@/core/utils"

// Guest reports under one roof, laid out like Event Settings: a left sub-nav
// (icon rail → labels on wide canvases) drives the report shown on the right.
// The active section lives in the URL (?tab=) so it's linkable; the old
// standalone routes redirect here. Each report card carries a single Export ▾
// dropdown (PDF / Word / CSV) for that report.
type ReportTab = "rsvp" | "dietary" | "accommodations" | "emergency" | "comms" | "custom" | "shared"

const NAV: Array<{ id: ReportTab; label: string; icon: ReactNode }> = [
  { id: "rsvp", label: "RSVPs", icon: <MailCheck /> },
  { id: "dietary", label: "Dietary", icon: <Utensils /> },
  { id: "accommodations", label: "Accommodations", icon: <Accessibility /> },
  { id: "emergency", label: "Emergency", icon: <Shield /> },
  { id: "comms", label: "Comms reach", icon: <Radio /> },
  { id: "custom", label: "Custom", icon: <Table2 /> },
]

export const Route = createFileRoute("/$orgSlug/$eventSlug/reports")({
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab: ReportTab; report?: string; create?: boolean } => ({
    tab:
      s.tab === "rsvp" ||
      s.tab === "accommodations" ||
      s.tab === "emergency" ||
      s.tab === "comms" ||
      s.tab === "custom" ||
      s.tab === "shared"
        ? s.tab
        : "dietary",
    // saved custom report deep link — only meaningful on the custom tab
    ...(s.tab === "custom" && typeof s.report === "string" && s.report
      ? { report: s.report }
      : {}),
    // the Create Report modal (New report entry point)
    ...(s.tab === "custom" && (s.create === true || s.create === "1") ? { create: true } : {}),
  }),
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient.ensureQueryData(formsQueryOptions(eventId)).catch(() => undefined)
      void context.queryClient.ensureQueryData(guestsQueryOptions(eventId)).catch(() => undefined)
      void context.queryClient
        .ensureQueryData(eventDietaryReportQueryOptions(eventId))
        .catch(() => undefined)
      void context.queryClient
        .ensureQueryData(eventAccessibilityReportQueryOptions(eventId))
        .catch(() => undefined)
      void context.queryClient
        .ensureQueryData(commsReportQueryOptions(eventId))
        .catch(() => undefined)
      void context.queryClient
        .ensureQueryData(reportDatasetQueryOptions(eventId))
        .catch(() => undefined)
      void context.queryClient
        .ensureQueryData(savedReportsQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: ReportsView,
})

function ReportsView() {
  const { tab, report, create } = Route.useSearch()
  const params = useParams({ from: "/$orgSlug/$eventSlug/reports" })
  const navigate = useNavigate()
  const { eventId } = useActiveEvent()
  const savedQ = useQuery({ ...savedReportsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const setTab = (t: ReportTab) =>
    void navigate({
      to: "/$orgSlug/$eventSlug/reports",
      params,
      search: { tab: t },
      replace: true,
    })
  // Select a saved custom report (null = a fresh, unsaved report)
  const openReport = (id: string | null) =>
    void navigate({
      to: "/$orgSlug/$eventSlug/reports",
      params,
      search: id ? { tab: "custom", report: id } : { tab: "custom" },
      replace: true,
    })
  const openCreate = () =>
    void navigate({
      to: "/$orgSlug/$eventSlug/reports",
      params,
      search: { tab: "custom", create: true },
      replace: true,
    })

  return (
    <div className="relative flex h-full min-h-0">
      {/* sub-nav — collapses to an icon rail on narrow canvases (mirrors Settings) */}
      <nav className="flex w-[58px] shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-plum-200/50 p-2 @[560px]:w-[200px] @[560px]:p-3 dark:border-white/10">
        <p className="mb-1 hidden px-2 text-[11px] font-semibold uppercase tracking-wide text-plum-700/40 @[560px]:block dark:text-plum-200/30">
          Reports
        </p>
        {NAV.map((item) => (
          <div key={item.id} className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={() => setTab(item.id)}
              title={item.label}
              aria-current={tab === item.id ? "page" : undefined}
              className={cn(
                "group flex items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] font-medium text-plum-800/80 transition hover:bg-plum-100/60 @[560px]:px-3 dark:text-plum-200/70 dark:hover:bg-white/5",
                tab === item.id &&
                  !(item.id === "custom" && report) &&
                  "bg-[var(--accent)]/12 font-semibold text-[var(--accent)] dark:text-plum-100",
              )}
            >
              <span className="grid h-5 w-5 shrink-0 place-items-center [&>svg]:h-[18px] [&>svg]:w-[18px]">
                {item.icon}
              </span>
              <span className="hidden truncate @[560px]:block">{item.label}</span>
            </button>
            {/* Saved custom reports live as children of the Custom tab. Hidden
                on the narrow icon rail (no room for text-only children). */}
            {item.id === "custom" && (savedQ.data?.length ?? 0) > 0 && (
              <div className="hidden flex-col gap-0.5 @[560px]:flex">
                {(savedQ.data ?? []).map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => openReport(r.id)}
                    title={r.name}
                    aria-current={tab === "custom" && report === r.id ? "page" : undefined}
                    className={cn(
                      "ml-[30px] truncate rounded-md px-2 py-1 text-left text-[12.5px] text-plum-700/70 transition hover:bg-plum-100/60 dark:text-plum-200/60 dark:hover:bg-white/5",
                      tab === "custom" &&
                        report === r.id &&
                        "bg-[var(--accent)]/12 font-semibold text-[var(--accent)] dark:text-plum-100",
                    )}
                  >
                    {r.name}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={openCreate}
                  className="ml-[30px] flex items-center gap-1 rounded-md px-2 py-1 text-left text-[12.5px] text-plum-500/70 transition hover:bg-plum-100/60 dark:text-plum-300/50 dark:hover:bg-white/5"
                >
                  <Plus className="h-3 w-3 shrink-0" /> New report
                </button>
              </div>
            )}
          </div>
        ))}

        {/* "Shared access" is pinned to the bottom, separated from the report
            list — it's a cross-cutting management view, not another report. */}
        {/* Divider, not a border on the button — a border-t under a rounded,
            fillable row reads as a broken card edge when it's selected. */}
        <hr className="mt-auto mb-1 border-0 border-t border-plum-200/50 dark:border-white/10" />
        <button
          type="button"
          onClick={() => setTab("shared")}
          title="Shared access"
          aria-current={tab === "shared" ? "page" : undefined}
          className={cn(
            "group flex items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] font-medium text-plum-800/80 transition hover:bg-plum-100/60 @[560px]:px-3 dark:text-plum-200/70 dark:hover:bg-white/5",
            tab === "shared" &&
              "bg-[var(--accent)]/12 font-semibold text-[var(--accent)] dark:text-plum-100",
          )}
        >
          <span className="grid h-5 w-5 shrink-0 place-items-center [&>svg]:h-[18px] [&>svg]:w-[18px]">
            <Link2 />
          </span>
          <span className="hidden truncate @[560px]:block">Shared access</span>
        </button>
      </nav>

      {/* Each report card owns a single Export ▾ dropdown (PDF / Word / CSV). */}
      <div className="relative flex min-w-0 flex-1">
        {tab === "rsvp" && <RsvpReport />}
        {tab === "dietary" && <DietaryReport />}
        {tab === "accommodations" && <AccessibilityReport />}
        {tab === "emergency" && <SystemFormReport {...EMERGENCY_REPORT} />}
        {tab === "comms" && <CommsReport />}
        {tab === "custom" && (
          <CustomReports
            reportId={report}
            onSelectReport={openReport}
            createOpen={create === true}
            onCreateClose={() => openReport(null)}
          />
        )}
        {tab === "shared" && <SharedAccessPanel />}
      </div>
    </div>
  )
}
